// src/features/auth/types/security.types.ts

/**
 * Security-related type definitions
 */

/**
 * Encryption algorithms
 */
export enum EncryptionAlgorithm {
  AES_GCM = 'AES-GCM',
  AES_CBC = 'AES-CBC',
  RSA_OAEP = 'RSA-OAEP',
}

/**
 * Hash algorithms
 */
export enum HashAlgorithm {
  SHA_256 = 'SHA-256',
  SHA_384 = 'SHA-384',
  SHA_512 = 'SHA-512',
}

/**
 * Key types
 */
export enum KeyType {
  SYMMETRIC = 'symmetric',
  PUBLIC = 'public',
  PRIVATE = 'private',
}

/**
 * Device fingerprint data
 */
export interface DeviceFingerprint {
  id: string;
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  hardwareConcurrency: number;
  deviceMemory?: number;
  touchSupport: boolean;
  webGLFingerprint?: string;
  canvasFingerprint?: string;
  audioFingerprint?: string;
  fonts?: string[];
  plugins?: string[];
  createdAt: string;
}

/**
 * Trusted device
 */
export interface TrustedDevice {
  id: string;
  deviceFingerprint: string;
  deviceName: string;
  trustLevel: TrustLevel;
  trustedAt: string;
  lastUsed: string;
  expiresAt?: string;
  location?: DeviceLocation;
}

/**
 * Device trust levels
 */
export enum TrustLevel {
  NONE = 'none',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  FULL = 'full',
}

/**
 * Device location
 */
export interface DeviceLocation {
  ip: string;
  country?: string;
  region?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * CSRF token data
 */
export interface CSRFToken {
  token: string;
  expiresAt: string;
  sessionId: string;
}

/**
 * Security event types
 */
export enum SecurityEventType {
  LOGIN_SUCCESS = 'login_success',
  LOGIN_FAILURE = 'login_failure',
  LOGOUT = 'logout',
  TOKEN_REFRESH = 'token_refresh',
  TOKEN_EXPIRED = 'token_expired',
  PASSWORD_CHANGE = 'password_change',
  PASSWORD_RESET = 'password_reset',
  MFA_ENABLED = 'mfa_enabled',
  MFA_DISABLED = 'mfa_disabled',
  MFA_CHALLENGE_SUCCESS = 'mfa_challenge_success',
  MFA_CHALLENGE_FAILURE = 'mfa_challenge_failure',
  DEVICE_TRUSTED = 'device_trusted',
  DEVICE_REVOKED = 'device_revoked',
  SUSPICIOUS_ACTIVITY = 'suspicious_activity',
  ACCOUNT_LOCKED = 'account_locked',
  ACCOUNT_UNLOCKED = 'account_unlocked',
}

/**
 * Security event
 */
export interface SecurityEvent {
  id: string;
  type: SecurityEventType;
  userId?: string;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
  deviceFingerprint?: string;
  metadata?: Record<string, any>;
  risk?: RiskAssessment;
}

/**
 * Risk assessment
 */
export interface RiskAssessment {
  score: number; // 0-100
  level: RiskLevel;
  factors: RiskFactor[];
  recommendations?: string[];
}

/**
 * Risk levels
 */
export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Risk factors
 */
export interface RiskFactor {
  type: string;
  description: string;
  weight: number;
  value: any;
}

/**
 * Security headers
 */
export interface SecurityHeaders {
  'X-Frame-Options'?: string;
  'X-Content-Type-Options'?: string;
  'X-XSS-Protection'?: string;
  'Strict-Transport-Security'?: string;
  'Content-Security-Policy'?: string;
  'Referrer-Policy'?: string;
  'Permissions-Policy'?: string;
}

/**
 * Rate limit configuration
 */
export interface RateLimitConfig {
  windowMs: number;
  maxRequests: number;
  keyGenerator?: (identifier: string) => string;
  skipSuccessfulRequests?: boolean;
  skipFailedRequests?: boolean;
}

/**
 * Rate limit status
 */
export interface RateLimitStatus {
  limit: number;
  remaining: number;
  reset: Date;
  retryAfter?: number;
}

/**
 * Encryption result
 */
export interface EncryptionResult {
  ciphertext: string;
  iv: string;
  salt?: string;
  tag?: string;
  algorithm: EncryptionAlgorithm;
}

/**
 * Key derivation options
 */
export interface KeyDerivationOptions {
  salt: string;
  iterations: number;
  keyLength: number;
  algorithm: HashAlgorithm;
}

/**
 * Password strength result
 */
export interface PasswordStrengthResult {
  score: number; // 0-4
  strength: PasswordStrength;
  feedback: PasswordFeedback;
  estimatedCrackTime: string;
}

/**
 * Password strength levels
 */
export enum PasswordStrength {
  VERY_WEAK = 'very_weak',
  WEAK = 'weak',
  FAIR = 'fair',
  STRONG = 'strong',
  VERY_STRONG = 'very_strong',
}

/**
 * Password feedback
 */
export interface PasswordFeedback {
  warning?: string;
  suggestions: string[];
}

/**
 * Security policy
 */
export interface SecurityPolicy {
  passwordPolicy: PasswordPolicy;
  sessionPolicy: SessionPolicy;
  mfaPolicy: MFAPolicy;
  devicePolicy: DevicePolicy;
  rateLimitPolicy: RateLimitPolicy;
}

/**
 * Password policy (extends from auth.types)
 */
export interface PasswordPolicy {
  minLength: number;
  maxLength?: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  preventReuse: number;
  expiryDays?: number;
  preventCommonPasswords: boolean;
  preventUserInfo: boolean;
}

/**
 * Session policy
 */
export interface SessionPolicy {
  maxDuration: number; // seconds
  idleTimeout: number; // seconds
  maxConcurrentSessions?: number;
  requireReauthForSensitive: boolean;
  rememberMeDuration?: number; // seconds
}

/**
 * MFA policy
 */
export interface MFAPolicy {
  required: boolean;
  allowedMethods: string[];
  gracePeriod?: number; // seconds
  trustedDeviceDuration?: number; // seconds
  backupCodesCount: number;
}

/**
 * Device policy
 */
export interface DevicePolicy {
  requireDeviceFingerprint: boolean;
  maxTrustedDevices: number;
  trustDuration: number; // seconds
  requireLocationCheck: boolean;
  allowedLocations?: string[];
}

/**
 * Rate limit policy
 */
export interface RateLimitPolicy {
  login: RateLimitConfig;
  register: RateLimitConfig;
  passwordReset: RateLimitConfig;
  api: RateLimitConfig;
}

/**
 * Security context
 */
export interface SecurityContext {
  isSecure: boolean;
  deviceTrusted: boolean;
  mfaVerified: boolean;
  riskLevel: RiskLevel;
  lastSecurityCheck: Date;
  activeThreats: SecurityThreat[];
}

/**
 * Security threat
 */
export interface SecurityThreat {
  id: string;
  type: string;
  severity: ThreatSeverity;
  description: string;
  detectedAt: Date;
  mitigations: string[];
}

/**
 * Threat severity
 */
export enum ThreatSeverity {
  INFO = 'info',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Audit log entry
 */
export interface AuditLogEntry {
  id: string;
  timestamp: Date;
  userId?: string;
  action: string;
  resource: string;
  result: AuditResult;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Audit result
 */
export enum AuditResult {
  SUCCESS = 'success',
  FAILURE = 'failure',
  ERROR = 'error',
}

/**
 * Security notification
 */
export interface SecurityNotification {
  id: string;
  type: SecurityNotificationType;
  title: string;
  message: string;
  severity: ThreatSeverity;
  timestamp: Date;
  requiresAction: boolean;
  actions?: SecurityAction[];
}

/**
 * Security notification types
 */
export enum SecurityNotificationType {
  NEW_DEVICE_LOGIN = 'new_device_login',
  SUSPICIOUS_ACTIVITY = 'suspicious_activity',
  PASSWORD_CHANGE = 'password_change',
  MFA_CHANGE = 'mfa_change',
  ACCOUNT_LOCKOUT = 'account_lockout',
  DATA_BREACH = 'data_breach',
}

/**
 * Security action
 */
export interface SecurityAction {
  id: string;
  label: string;
  action: string;
  critical?: boolean;
}